// Pure, framework-free "gamified" progress simulation for the S6 upload
// dropzone's currently-uploading row (product feedback: the old repeating
// 3-stage skeleton read as weak/fake).
//
// POST /supplier/uploads is SYNCHRONOUS and carries no real progress signal
// — a large official CBAM template can legitimately take 1-3 minutes
// server-side (see CommsTemplateDropzone's own doc comment) — so this
// drives a believable sweep through 5 NAMED phases instead of leaving the
// row on a frozen-looking indeterminate spinner.
//
// Model: the first four phases advance briskly on a fixed schedule (there
// is nothing real to key off of yet), landing the bar in the final,
// request-in-flight phase within ~3.7s. From there the percentage creeps
// ASYMPTOTICALLY toward a 90% ceiling for as long as the request stays in
// flight — see asymptoticPercent()'s doc comment for why it stays visibly
// climbing rather than parking at the ceiling, even across a 1-3 minute
// wait. `complete()` (call on the real 201) sweeps every phase to "done"
// and reports 100%; `freeze()` (call on a rejection) just stops the clock
// exactly where it stood — the caller swaps in its own failure UI, so a
// frozen bar is never actually rendered to the user.
//
// Framework/DOM-agnostic — only global `setTimeout`/`setInterval`/
// `Date.now`, all of which Jest's modern fake timers intercept — so this
// state machine is unit-testable without mounting anything.
// CommsTemplateDropzone owns the React wiring (one instance per actively
// uploading item) and the `prefers-reduced-motion` lookup: `reducedMotion`
// is a plain constructor flag here, this module never touches `window`.
// Under `reducedMotion`, the schedule only ever emits at a phase's own
// boundary (0/15/35/55/75/100) — never the continuously-creeping
// asymptotic climb — so there is no animated sweep, only discrete state
// changes; pair with a `motion-reduce:transition-none` bar so the
// component's own CSS width-transition doesn't animate either.

// i18n: display fields are getters (not plain strings) — this module is
// deliberately framework/DOM-agnostic (see file doc comment above), but
// i18n.t() itself has no React dependency (only the useTranslation() hook
// does), so a plain getter reads a live-translated value here exactly like
// Common/config/lockedFeatures.js's getters do for a non-component module.
import i18n from 'i18n';

export const UPLOAD_PHASES = Object.freeze([
  {
    key: 'securing',
    get label() {
      return i18n.t('freeSupplier:upload.phase.securing', 'Securing your workbook');
    },
  },
  {
    key: 'reading',
    get label() {
      return i18n.t('freeSupplier:upload.phase.reading', 'Reading the template');
    },
  },
  {
    key: 'extracting',
    get label() {
      return i18n.t('freeSupplier:upload.phase.extracting', 'Extracting emissions data');
    },
  },
  {
    key: 'benchmarking',
    get label() {
      return i18n.t('freeSupplier:upload.phase.benchmarking', 'Calculating SEFA benchmarks');
    },
  },
  {
    key: 'preparing',
    get label() {
      return i18n.t('freeSupplier:upload.phase.preparing', 'Preparing your drafts');
    },
  },
]);

// The percent the bar reads the MOMENT each phase starts. The last entry
// doubles as the floor asymptoticPercent() climbs from.
const PHASE_START_PERCENT = Object.freeze([0, 15, 35, 55, 75]);

// How long the simulation dwells in each of the first four phases before
// advancing to the next one (ms) — brisk and fixed, since there is no real
// signal to key off yet. One fewer entry than UPLOAD_PHASES: the final
// phase has no fixed duration, it holds until complete()/freeze() is called.
// Kept as exact multiples of FINE_TICK_INTERVAL_MS so a boundary crossing
// is always reported by the very next tick, not delayed by up to a whole
// tick interval.
const EARLY_PHASE_DURATION_MS = Object.freeze([750, 1000, 1250, 750]);
const TOTAL_EARLY_PHASE_DURATION_MS = EARLY_PHASE_DURATION_MS.reduce((sum, ms) => sum + ms, 0);

const ASYMPTOTE_CAP_PERCENT = 90;
// Tuned so the final phase is still visibly climbing well past 20s in
// (~83%) rather than reading as parked at the ceiling — a large real
// upload can take 1-3 minutes. At 20s: ~83%. At 60s: ~89%. At 180s: ~90%,
// but the formula is a strict asymptote — it never actually reaches it.
const ASYMPTOTE_TAU_MS = 25_000;
const FINE_TICK_INTERVAL_MS = 250;

/**
 * The final phase's percent after `elapsedMs` spent in it, asymptotically
 * approaching (but mathematically never reaching) ASYMPTOTE_CAP_PERCENT.
 * Exported for direct unit coverage of the curve's shape/pacing.
 */
export function asymptoticPercent(elapsedMs) {
  const floor = PHASE_START_PERCENT[PHASE_START_PERCENT.length - 1];
  const range = ASYMPTOTE_CAP_PERCENT - floor;
  const progress = 1 - Math.exp(-Math.max(0, elapsedMs) / ASYMPTOTE_TAU_MS);
  return floor + range * progress;
}

// Which of the 5 phases the fixed early schedule has reached by
// `elapsedMs` — clamped to the final (asymptotic) phase once every fixed
// duration has elapsed.
function phaseIndexAtElapsed(elapsedMs) {
  let acc = 0;
  for (let i = 0; i < EARLY_PHASE_DURATION_MS.length; i += 1) {
    acc += EARLY_PHASE_DURATION_MS[i];
    if (elapsedMs < acc) return i;
  }
  return UPLOAD_PHASES.length - 1;
}

function percentAtElapsed(elapsedMs) {
  const phaseIndex = phaseIndexAtElapsed(elapsedMs);
  if (phaseIndex < UPLOAD_PHASES.length - 1) return PHASE_START_PERCENT[phaseIndex];
  return asymptoticPercent(elapsedMs - TOTAL_EARLY_PHASE_DURATION_MS);
}

function buildSnapshot(phaseIndex, percent, allComplete) {
  return {
    percent,
    phaseIndex,
    phases: UPLOAD_PHASES.map((phase, index) => ({
      key: phase.key,
      label: phase.label,
      complete: allComplete || index < phaseIndex,
      active: !allComplete && index === phaseIndex,
    })),
  };
}

/**
 * One timer-driven simulation instance per actively-uploading row.
 * `onChange(snapshot)` fires on every state change.
 *
 * - start(): begins the schedule at phase 0 / 0%.
 * - complete(): the real request succeeded — sweeps directly to phase
 *   `UPLOAD_PHASES.length - 1` / 100%, every phase marked complete, and
 *   stops the clock. Idempotent past the first call.
 * - freeze(): the real request failed — stops the clock with no further
 *   emission (the caller swaps to its own failure UI). Idempotent.
 * - dispose(): unmount-safety alias for freeze() (stop timers, no emit).
 */
export function createUploadPhaseSimulation({ onChange, reducedMotion = false } = {}) {
  let settled = false;
  let startedAt = null;
  let intervalId = null;
  const timeoutIds = [];

  const emit = (phaseIndex, percent, allComplete = false) => {
    onChange?.(buildSnapshot(phaseIndex, percent, allComplete));
  };

  const clearTimers = () => {
    if (intervalId !== null) {
      clearInterval(intervalId);
      intervalId = null;
    }
    while (timeoutIds.length) clearTimeout(timeoutIds.pop());
  };

  const start = () => {
    startedAt = Date.now();
    emit(0, PHASE_START_PERCENT[0]);

    if (reducedMotion) {
      // Discrete-only: schedule exactly the 4 phase-boundary transitions,
      // no continuously-creeping asymptotic climb in between — the bar
      // only ever jumps between the 5 named phases' own percentages.
      let acc = 0;
      EARLY_PHASE_DURATION_MS.forEach((duration, index) => {
        acc += duration;
        const phaseIndex = index + 1;
        timeoutIds.push(setTimeout(() => emit(phaseIndex, PHASE_START_PERCENT[phaseIndex]), acc));
      });
      return;
    }

    intervalId = setInterval(() => {
      const elapsed = Date.now() - startedAt;
      emit(phaseIndexAtElapsed(elapsed), percentAtElapsed(elapsed));
    }, FINE_TICK_INTERVAL_MS);
  };

  const complete = () => {
    if (settled) return;
    settled = true;
    clearTimers();
    emit(UPLOAD_PHASES.length - 1, 100, true);
  };

  const freeze = () => {
    if (settled) return;
    settled = true;
    clearTimers();
  };

  return { start, complete, freeze, dispose: freeze };
}
